import { Control } from 'react-hook-form';
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Check } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface Step1BasicInfoProps {
  control: Control<any>;
  form: any;
  onNext: () => void;
  onCancel: () => void;
  onDataExtracted: (data: any) => void;
  campaignId: string | null;
  isAnalysisRunning?: boolean;
  onAnalysisComplete?: () => void;
  onEnsureDraft?: () => Promise<void>;
}

export const Step1BasicInfo = ({
  form,
  onNext,
  campaignId,
  onEnsureDraft,
}: Step1BasicInfoProps) => {
  const [activeTab, setActiveTab] = useState<'text' | 'file'>('file');
  const [isUploadingCV, setIsUploadingCV] = useState(false);
  const [isUploadingJob, setIsUploadingJob] = useState(false);
  const cvFileInputRef = useRef<HTMLInputElement>(null);
  const jobFileInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();
  const { toast } = useToast();

  // Surveiller les valeurs nécessaires
  const cvPath = form.watch('candidate_cv_url');
  const jobDescriptionFilePath = form.watch('job_description_file_url');
  const jobDescriptionText = form.watch('job_description_text');

  // Déterminer si les fichiers sont déjà uploadés
  const cvUploaded = !!cvPath;
  const jobFileUploaded = !!jobDescriptionFilePath;

  // Note: L'analyse IA se fera maintenant dans la modal, pas automatiquement ici

  // Permettre de passer à l'étape suivante dès que les documents sont uploadés
  const canGoNext = (cvUploaded && (jobFileUploaded || (jobDescriptionText && jobDescriptionText.trim() !== '')));

  // Fonction d'upload de fichier
  const uploadFile = async (file: File, type: 'cv' | 'job'): Promise<string> => {
    if (!user?.id) throw new Error('Utilisateur non connecté');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${type}-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('campaign-documents')
      .upload(fileName, file);

    if (uploadError) {
      throw new Error(`Erreur d'upload: ${uploadError.message}`);
    }

    return fileName;
  };

  const validateFile = (file: File) => {
    const allowedTypes = ['application/pdf'];
    return allowedTypes.includes(file.type);
  };

  // Handlers pour l'upload de fichiers
  const handleCVUpload = async (file: File) => {
    if (!validateFile(file)) {
      toast({
        title: "Format de fichier non supporté",
        description: "Veuillez utiliser un fichier PDF uniquement.",
        variant: "destructive",
      });
      return;
    }

    if (!campaignId) {
      await onEnsureDraft?.();
    }

    setIsUploadingCV(true);
    try {
      const filePath = await uploadFile(file, 'cv');
      form.setValue('candidate_cv_url', filePath);
      toast({
        title: "CV uploadé avec succès",
        description: "Le CV a été téléchargé et sera analysé par l'IA.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur d'upload",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploadingCV(false);
    }
  };

  const handleJobDescriptionUpload = async (file: File) => {
    if (!validateFile(file)) {
      toast({
        title: "Format de fichier non supporté",
        description: "Veuillez utiliser un fichier PDF uniquement.",
        variant: "destructive",
      });
      return;
    }

    if (!campaignId) {
      await onEnsureDraft?.();
    }

    setIsUploadingJob(true);
    try {
      const filePath = await uploadFile(file, 'job');
      form.setValue('job_description_file_url', filePath);
      toast({
        title: "Fiche de poste uploadée avec succès",
        description: "La fiche de poste a été téléchargée et sera analysée par l'IA.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur d'upload",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploadingJob(false);
    }
  };

  const handleCVClick = () => {
    cvFileInputRef.current?.click();
  };

  const handleJobFileClick = () => {
    jobFileInputRef.current?.click();
  };

  const handleCVFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleCVUpload(file);
    }
  };

  const handleJobFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleJobDescriptionUpload(file);
    }
  };

  // Handlers pour le drag and drop
  const handleCVDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCVDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleCVUpload(files[0]);
    }
  };

  const handleJobDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleJobDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleJobDescriptionUpload(files[0]);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl space-y-8">
        {/* Section Header */}
        <div className="space-y-2">
          <h1 className="text-2xl font-medium font-bricolage text-gray-800">Télécharger des documents</h1>
          <p className="text-sm font-normal font-bricolage text-neutral-400">
            Téléchargez le CV et la description du poste du candidat. L'IA extraira automatiquement les informations du candidat (nom, adresse e-mail), le poste visé et les références recommandées en fonction de l'expérience la plus pertinente.
          </p>
        </div>

        {/* Content Section */}
        <div className="space-y-6">
          {/* CV du candidat */}
          <div className="space-y-4">
            <div className="text-gray-800 text-base font-medium font-bricolage leading-tight">
              CV du candidat
            </div>
            <div className="h-48 flex flex-col justify-start items-start gap-1">
              <div
                onClick={handleCVClick}
                onDragOver={handleCVDragOver}
                onDrop={handleCVDrop}
                className="w-full flex-1 rounded-[10px] border border-teal-400 flex flex-col justify-center items-center gap-3 cursor-pointer hover:bg-teal-50/50 transition-colors"
              >
                <div className="relative">
                  {isUploadingCV ? (
                    <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
                  ) : cvUploaded ? (
                    <Check className="w-6 h-6 text-green-600" />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.32007 12.3201L11.8801 9.76007L14.4401 12.3201" stroke="#40E0D0" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M11.8799 20V9.83" stroke="#40E0D0" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M20 11.8201C20 7.40007 17 3.82007 12 3.82007C7 3.82007 4 7.40007 4 11.8201" stroke="#40E0D0" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="text-neutral-400 text-sm font-medium font-bricolage leading-none text-center px-4">
                  {isUploadingCV ? 'Upload en cours...' :
                    cvUploaded ? 'CV téléchargé avec succès - Cliquez pour changer' :
                      'Faites glisser et déposez votre CV ici ou cliquez pour parcourir vos fichiers'}
                </div>
                <div className="text-neutral-400 text-[10px] font-normal font-bricolage">
                  Formats acceptés : PDF uniquement
                </div>
              </div>
              <div className="text-neutral-400 text-xs font-light font-bricolage leading-tight">
                L'IA extraira automatiquement les informations du candidat.
              </div>
              <input
                ref={cvFileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleCVFileChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Description du poste */}
          <div className="space-y-3">
            <div className="text-gray-800 text-base font-medium font-bricolage leading-tight">
              Description du poste
            </div>
            <div className="flex flex-col justify-start items-start gap-4">
              {/* Tabs */}
              <div className="inline-flex justify-start items-center gap-3">
                <button
                  onClick={() => setActiveTab('text')}
                  className={`p-2.5 rounded-lg border flex justify-center items-center gap-1 overflow-hidden transition-colors ${activeTab === 'text'
                    ? 'bg-teal-400/10 border-teal-400'
                    : 'border-neutral-400/20'
                    }`}
                >
                  <div className={`text-xs font-medium font-bricolage ${activeTab === 'text' ? 'text-teal-400' : 'text-neutral-400'
                    }`}>
                    Saisie de texte
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('file')}
                  className={`p-2.5 rounded-lg border flex justify-center items-center gap-1 overflow-hidden transition-colors ${activeTab === 'file'
                    ? 'bg-teal-400/10 border-teal-400'
                    : 'border-neutral-400/20'
                    }`}
                >
                  <div className={`text-xs font-medium font-bricolage ${activeTab === 'file' ? 'text-teal-400' : 'text-neutral-400'
                    }`}>
                    Télécharger le fichier
                  </div>
                </button>
              </div>

              {/* Content based on active tab */}
              <div className="w-full h-48 flex flex-col justify-start items-start gap-1">
                {activeTab === 'file' ? (
                  <div
                    onClick={handleJobFileClick}
                    onDragOver={handleJobDragOver}
                    onDrop={handleJobDrop}
                    className="w-full flex-1 rounded-[10px] border border-teal-400 flex flex-col justify-center items-center gap-3 cursor-pointer hover:bg-teal-50/50 transition-colors"
                  >
                    <div className="relative">
                      {isUploadingJob ? (
                        <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
                      ) : jobFileUploaded ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.32007 12.3201L11.8801 9.76007L14.4401 12.3201" stroke="#40E0D0" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M11.8799 20V9.83" stroke="#40E0D0" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M20 11.8201C20 7.40007 17 3.82007 12 3.82007C7 3.82007 4 7.40007 4 11.8201" stroke="#40E0D0" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div className="text-neutral-400 text-sm font-medium font-bricolage leading-none text-center px-4">
                      {isUploadingJob ? 'Upload en cours...' :
                        jobFileUploaded ? 'Fichier téléchargé avec succès - Cliquez pour changer' :
                          'Faites glisser et déposez votre fiche de poste ici ou cliquez pour parcourir vos fichiers'}
                    </div>
                    <div className="text-neutral-400 text-[10px] font-normal font-bricolage">
                      Formats acceptés : PDF uniquement
                    </div>
                  </div>
                ) : (
                  <textarea
                    className="w-full flex-1 rounded-[10px] border border-teal-400 p-4 text-sm font-bricolage resize-none focus:outline-none focus:ring-2 focus:ring-teal-400/20"
                    placeholder="Collez ici la description du poste..."
                    value={jobDescriptionText || ''}
                    onChange={(e) => form.setValue('job_description_text', e.target.value)}
                  />
                )}
                <div className="text-neutral-400 text-xs font-light font-bricolage leading-tight">
                  L'IA extraira automatiquement les informations du candidat.
                </div>
                {activeTab === 'file' && (
                  <input
                    ref={jobFileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleJobFileChange}
                    className="hidden"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with button */}
        <div className="flex justify-end items-center">
          <Button
            onClick={canGoNext ? onNext : undefined}
            disabled={!canGoNext}
            className="h-10 px-5 py-3 bg-teal-400 hover:bg-teal-500 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-white text-sm font-semibold font-bricolage">
              Suivant
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};
